import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, Edit3, Send, BarChart3, Users, Shield, ChevronRight, ChevronLeft } from 'lucide-react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAuth } from '../../contexts/AuthContext';

const FeatureTourStep = ({ onNext }) => {
  const { completeStep } = useOnboarding();
  const { completeOnboardingStep } = useAuth();
  const [currentFeature, setCurrentFeature] = useState(0);

  const features = [
    {
      icon: <Upload className="w-12 h-12 text-blue-500" />,
      title: "Upload Documents",
      description: "Simply drag and drop your documents or click to upload. We support PDF, Word, and image formats.",
      highlights: [
        "Drag & drop support",
        "Multiple file formats",
        "Automatic preview generation",
        "Secure cloud storage"
      ],
      mockup: "🔄 Document upload interface"
    },
    {
      icon: <Edit3 className="w-12 h-12 text-green-500" />,
      title: "Add Signature Fields",
      description: "Place signature fields exactly where you need them. Assign different fields to different signers.",
      highlights: [
        "Visual field placement",
        "Multiple signature types",
        "Assign to specific signers",
        "Required vs optional fields"
      ],
      mockup: "📝 Signature field editor"
    },
    {
      icon: <Send className="w-12 h-12 text-purple-500" />,
      title: "Send for Signatures",
      description: "Invite others to sign with customizable email invitations. Track who has signed and who hasn't.",
      highlights: [
        "Custom email invitations",
        "Automatic reminders",
        "Real-time status tracking",
        "Signing order control"
      ],
      mockup: "📧 Signature request system"
    },
    {
      icon: <BarChart3 className="w-12 h-12 text-orange-500" />,
      title: "Track Progress",
      description: "Monitor signature status, view completion rates, and get detailed analytics on your documents.",
      highlights: [
        "Real-time status updates",
        "Completion analytics",
        "Performance metrics",
        "Export reports"
      ],
      mockup: "📊 Analytics dashboard"
    },
    {
      icon: <Users className="w-12 h-12 text-pink-500" />,
      title: "Collaborate Safely",
      description: "Share documents with team members, set permissions, and maintain complete control over access.",
      highlights: [
        "Team collaboration",
        "Permission controls",
        "Secure sharing",
        "Access management"
      ],
      mockup: "👥 Team collaboration tools"
    },
    {
      icon: <Shield className="w-12 h-12 text-red-500" />,
      title: "Contract Analysis",
      description: "AI-powered contract review identifies potential issues and suggests improvements automatically.",
      highlights: [
        "AI risk assessment",
        "Clause recommendations",
        "Legal compliance checks",
        "Automated redlining"
      ],
      mockup: "🔍 AI contract analysis"
    }
  ];

  const handlePrevious = () => {
    setCurrentFeature(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    if (currentFeature < features.length - 1) {
      setCurrentFeature(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    try {
      console.log('🚀 FeatureTourStep: Completing feature tour...');
      // Complete both locally and on backend
      await completeOnboardingStep('featureTour');
      completeStep('featureTour');
      console.log('✅ FeatureTourStep: Feature tour completed successfully');
      onNext();
    } catch (error) {
      console.error('❌ FeatureTourStep: Error completing feature tour:', error);
      // Still proceed with onboarding even if API call fails
      completeStep('featureTour');
      onNext();
    }
  };

  const currentFeatureData = features[currentFeature];

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Discover SignPact's key features
        </h2>
        <p className="text-lg text-gray-600">
          Get familiar with the tools that will streamline your document workflow
        </p>
      </motion.div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Feature Navigation */}
        <div className="bg-gray-50 border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {features.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentFeature(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentFeature
                      ? 'bg-blue-500'
                      : index < currentFeature
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-gray-500">
              {currentFeature + 1} of {features.length}
            </span>
          </div>
        </div>

        {/* Feature Content */}
        <motion.div
          key={currentFeature}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="p-8"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Feature Info */}
            <div>
              <div className="flex items-center space-x-4 mb-6">
                {currentFeatureData.icon}
                <h3 className="text-2xl font-bold text-gray-900">
                  {currentFeatureData.title}
                </h3>
              </div>
              
              <p className="text-lg text-gray-600 mb-6">
                {currentFeatureData.description}
              </p>

              <div className="space-y-3">
                {currentFeatureData.highlights.map((highlight, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700">{highlight}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Feature Visual */}
            <div className="bg-gray-100 rounded-lg p-8 text-center">
              <div className="text-6xl mb-4">{currentFeatureData.mockup}</div>
              <p className="text-gray-600">
                Interactive demo coming soon
              </p>
            </div>
          </div>
        </motion.div>

        {/* Navigation Controls */}
        <div className="bg-gray-50 border-t border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentFeature === 0}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                currentFeature === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ChevronLeft size={16} />
              <span>Previous</span>
            </button>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleComplete()}
                className="px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                Skip tour
              </button>
              
              <button
                onClick={handleNext}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <span>
                  {currentFeature === features.length - 1 ? 'Finish Tour' : 'Next'}
                </span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mt-6 text-center"
      >
        <p className="text-sm text-gray-500">
          💡 Pro tip: You can access feature help anytime from the dashboard
        </p>
      </motion.div>
    </div>
  );
};

export default FeatureTourStep;